import { c as _c } from "react/compiler-runtime";
import { isEqual } from 'lodash';
import { useCallback, useState } from 'react';
import { ViewConfiguration } from '../../BalanceSheetTable/BalanceSheetTableContext';
import { BalanceSheetConfigurationUpdate, RefetchQueryDescriptor, UpdateConfigurationFn } from '../BalanceSheetConfigPanel.types';
export function computeUpdatedConfig(prev: ViewConfiguration, field: keyof ViewConfiguration, value: boolean): ViewConfiguration {
  const updated = {
    ...prev,
    [field]: value
  };
  if (field === 'showInEstate' && !value) {
    updated.separateInEstateDirectlyHeld = false;
    updated.separateInEstateTrusts = false;
  }
  if (field === 'showOutOfEstate' && !value) {
    updated.separateOutOfEstateTrusts = false;
  }
  return updated;
}
interface UseDraftConfigArgs {
  viewConfigurationId: string | null;
  /** Keep edits local until the panel saves them on Done. */
  saveOnDone?: boolean;
  setViewConfiguration: (config: ViewConfiguration) => void;
  updateConfiguration: UpdateConfigurationFn;
  refetchDocuments: RefetchQueryDescriptor[];
  reportError: (message: string, error: Error) => void;
  showFeedback: (message: string) => void;
}
export interface UseDraftConfigReturn {
  /** Source of truth for the form UI (not synced from server during editing) */
  config: ViewConfiguration | null;
  /** Applies dependent resets, pushes to context, and eager-saves to backend.
   *  Use for structural toggles that require column regeneration. */
  updateField: (field: keyof ViewConfiguration, value: boolean) => void;
  /** Updates local state + context for live preview without eager-saving.
   *  Use for display-only fields (displayName, showSummaryMetrics,
   *  showExpandedAssetCategories) — persisted only on explicit save. */
  updateLocal: (update: Partial<ViewConfiguration>) => void;
  applyConfig: (config: ViewConfiguration) => Promise<unknown>;
  isDirty: boolean;
  commitSnapshot: () => void;
  /** Reverts local state AND restores the backend (toggles were eagerly saved) */
  revert: () => ViewConfiguration | null;
  reinitialize: (config: ViewConfiguration | null) => void;
}
export function useDraftConfig(t0) {
  const $ = _c(40);
  const {
    viewConfigurationId,
    saveOnDone: t1,
    setViewConfiguration,
    updateConfiguration,
    refetchDocuments,
    reportError,
    showFeedback
  } = t0;
  const saveOnDone = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== refetchDocuments || $[1] !== reportError || $[2] !== saveOnDone || $[3] !== showFeedback || $[4] !== updateConfiguration || $[5] !== viewConfigurationId) {
    t2 = update => {
      if (saveOnDone || !viewConfigurationId) {
        return Promise.resolve();
      }
      return updateConfiguration({
        id: viewConfigurationId,
        update,
        refetchQueries: refetchDocuments,
        awaitRefetchQueries: true
      }).catch(error => {
        reportError("Failed to update configuration", error as Error);
        showFeedback("Failed to update configuration");
      });
    };
    $[0] = refetchDocuments;
    $[1] = reportError;
    $[2] = saveOnDone;
    $[3] = showFeedback;
    $[4] = updateConfiguration;
    $[5] = viewConfigurationId;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const saveEagerly = t2;
  const [serverConfig, setServerConfig] = useState(null);
  const [localConfig, setLocalConfig] = useState(null);
  let t3;
  if ($[7] !== localConfig || $[8] !== saveEagerly || $[9] !== setViewConfiguration) {
    t3 = (field, value) => {
      if (!localConfig) {
        return;
      }
      const updated = computeUpdatedConfig(localConfig, field, value);
      setLocalConfig(updated);
      setViewConfiguration(updated);
      saveEagerly(updated);
    };
    $[7] = localConfig;
    $[8] = saveEagerly;
    $[9] = setViewConfiguration;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const updateField = t3;
  let t4;
  if ($[11] !== localConfig || $[12] !== setViewConfiguration) {
    t4 = update_0 => {
      if (!localConfig) {
        return;
      }
      const updated_0 = {
        ...localConfig,
        ...update_0
      };
      setLocalConfig(updated_0);
      setViewConfiguration(updated_0);
    };
    $[11] = localConfig;
    $[12] = setViewConfiguration;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const updateLocal = t4;
  let t5;
  if ($[14] !== saveEagerly || $[15] !== setViewConfiguration) {
    t5 = config => {
      setLocalConfig(config);
      setViewConfiguration(config);
      return saveEagerly(config);
    };
    $[14] = saveEagerly;
    $[15] = setViewConfiguration;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const applyConfig = t5;
  let t6;
  bb0: {
    const original = serverConfig;
    if (!original || !localConfig) {
      t6 = false;
      break bb0;
    }
    let t7;
    if ($[17] !== localConfig || $[18] !== original) {
      t7 = isEqual(original, localConfig);
      $[17] = localConfig;
      $[18] = original;
      $[19] = t7;
    } else {
      t7 = $[19];
    }
    t6 = !t7;
  }
  const isDirty = t6;
  let t7;
  if ($[20] !== localConfig) {
    t7 = () => {
      setServerConfig(localConfig);
    };
    $[20] = localConfig;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const commitSnapshot = t7;
  let t8;
  if ($[22] !== refetchDocuments || $[23] !== reportError || $[24] !== saveOnDone || $[25] !== serverConfig || $[26] !== setViewConfiguration || $[27] !== showFeedback || $[28] !== updateConfiguration || $[29] !== viewConfigurationId) {
    t8 = () => {
      const original_0 = serverConfig;
      setLocalConfig(original_0);
      if (original_0) {
        setViewConfiguration(original_0);
      }
      if (original_0 && viewConfigurationId && !saveOnDone) {
        updateConfiguration({
          id: viewConfigurationId,
          update: original_0,
          refetchQueries: refetchDocuments,
          awaitRefetchQueries: true
        }).catch(error_0 => {
          reportError("Failed to restore configuration", error_0 as Error);
          showFeedback("Failed to restore configuration");
        });
      }
      return original_0;
    };
    $[22] = refetchDocuments;
    $[23] = reportError;
    $[24] = saveOnDone;
    $[25] = serverConfig;
    $[26] = setViewConfiguration;
    $[27] = showFeedback;
    $[28] = updateConfiguration;
    $[29] = viewConfigurationId;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  const revert = t8;
  let t9;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = config_0 => {
      setServerConfig(config_0);
      setLocalConfig(config_0);
    };
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  const reinitialize = t9;
  let t10;
  if ($[32] !== applyConfig || $[33] !== commitSnapshot || $[34] !== isDirty || $[35] !== localConfig || $[36] !== revert || $[37] !== updateField || $[38] !== updateLocal) {
    t10 = {
      config: localConfig,
      updateField,
      updateLocal,
      applyConfig,
      isDirty,
      commitSnapshot,
      revert,
      reinitialize
    };
    $[32] = applyConfig;
    $[33] = commitSnapshot;
    $[34] = isDirty;
    $[35] = localConfig;
    $[36] = revert;
    $[37] = updateField;
    $[38] = updateLocal;
    $[39] = t10;
  } else {
    t10 = $[39];
  }
  return t10;
}